import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchSessions, createSession as apiCreateSession, deleteSessionById, fetchSessionById } from '../api';
import { PAGE_NAME_OPTIONS, type PageNameOption } from '../api.types';
import { useLanguage, isSupportedLanguage } from './LanguageDropdown';

type SessionBasic = { id: string; name: string };

type SessionContextType = {
  selectedSession: SessionBasic | null;
  setSelectedSession: (session: SessionBasic | null) => void;
  hydrateSelectedSession?: (sessionId: string) => Promise<void>;
};

const SessionContext = createContext<SessionContextType>({
  selectedSession: null,
  setSelectedSession: () => {}
});

export const useSession = () => useContext(SessionContext);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedSession, setSelectedSession] = useState<SessionBasic | null>(null);
  const { selectedLanguage, setSelectedLanguage } = useLanguage();

  const hydrateSelectedSession = useCallback(async (sessionId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const data = await fetchSessionById(token, sessionId, selectedLanguage);
      // Store minimal session info; rest will be consumed by ChatInterface via actions
      setSelectedSession({ id: data.session.id, name: data.session.name });
      const sessionLanguage = data.session.language;
      if (sessionLanguage && isSupportedLanguage(sessionLanguage)) {
        setSelectedLanguage(sessionLanguage);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to hydrate session', e);
    }
  }, [selectedLanguage, setSelectedLanguage]);

  const value = useMemo(() => ({ selectedSession, setSelectedSession, hydrateSelectedSession }), [selectedSession, hydrateSelectedSession]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

const SessionDropdown: React.FC = () => {
  const { selectedSession, setSelectedSession } = useSession();
  const { selectedLanguage, setSelectedLanguage } = useLanguage();

  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [sessions, setSessions] = useState<SessionBasic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newWebsiteId, setNewWebsiteId] = useState('');
  const [newPageName, setNewPageName] = useState<PageNameOption | ''>('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
  }, [open]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const list = await fetchSessions(token);
      setSessions(list.map(s => ({ id: s.id, name: s.name })));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch sessions', e);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSessions().catch(() => {});
    }
  }, [open]);

  // Keep list in sync when selection changes (e.g., session auto-created elsewhere)
  useEffect(() => {
    loadSessions().catch((e) => {
      // eslint-disable-next-line no-console
      console.error('Failed to reload sessions', e);
      setError('Failed to reload sessions');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id]);

  useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
      setNewSessionName('');
      setNewWebsiteId('');
      setNewPageName('');
      setCreateError(null);
    }
  }, [open]);

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCreateError('Authorization token is missing.');
      return;
    }
    if (!newSessionName.trim()) {
      setCreateError('Session name is required.');
      return;
    }

    const trimmedWebsiteId = newWebsiteId.trim();
    const normalizedWebsiteId = trimmedWebsiteId === '' ? null : trimmedWebsiteId;
    const normalizedPageName = newPageName === '' ? null : newPageName;

    setCreateLoading(true);
    setCreateError(null);

    try {
      const session = await apiCreateSession(
        token,
        newSessionName.trim(),
        [],
        selectedLanguage,
        normalizedWebsiteId,
        normalizedPageName
      );
      const basicSession = { id: session.id, name: session.name };
      setSessions(prev => [basicSession, ...prev]);
      setSelectedSession(basicSession);

      setOpen(false);
      setShowCreateForm(false);
      setNewSessionName('');
      setNewWebsiteId('');
      setNewPageName('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create session', e);
      setCreateError(e instanceof Error ? e.message : 'Failed to create session');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      await deleteSessionById(token, sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete session', e);
      setError('Failed to delete session');
    }
  };

  const handleSelect = (session: SessionBasic) => {
    setSelectedSession(session);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setOpen((prev) => !prev);
    } else if (e.key === 'ArrowDown') {
      setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const dropdownList = (
    <div
      className="bg-white border border-[#EDF0F5] rounded-lg shadow-lg z-[9999] absolute"
      style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#EDF0F5]">
        <span className="text-[#464C5C] text-sm font-semibold">Sessions</span>
        <button
          className="text-[#165bfb] text-sm font-semibold hover:underline disabled:text-[#93C5FD]"
          onClick={() => {
            setShowCreateForm(prev => !prev);
            setCreateError(null);
          }}
          disabled={createLoading}
        >
          {showCreateForm ? 'Close' : '+ New'}
        </button>
      </div>
      {showCreateForm && (
        <form className="px-4 py-3 border-b border-[#EDF0F5] flex flex-col gap-3 bg-[#F6F8FF]" onSubmit={handleCreateSubmit}>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-[#6B7280]" htmlFor="new-session-name">Session name</label>
            <input
              id="new-session-name"
              type="text"
              value={newSessionName}
              onChange={(event) => {
                setNewSessionName(event.target.value);
                setCreateError(null);
              }}
              className="border border-[#EDF0F5] rounded-lg px-3 py-2 text-sm focus:border-[#165bfb] focus:ring-1 focus:ring-[#165bfb] outline-none"
              placeholder="Enter session name"
              disabled={createLoading}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-[#6B7280]" htmlFor="new-session-website">Website ID (optional)</label>
            <input
              id="new-session-website"
              type="text"
              value={newWebsiteId}
              onChange={(event) => {
                setNewWebsiteId(event.target.value);
                setCreateError(null);
              }}
              className="border border-[#EDF0F5] rounded-lg px-3 py-2 text-sm focus:border-[#165bfb] focus:ring-1 focus:ring-[#165bfb] outline-none"
              placeholder="Enter website id"
              disabled={createLoading}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-[#6B7280]" htmlFor="new-session-page">Page name (optional)</label>
            <select
              id="new-session-page"
              value={newPageName}
              onChange={(event) => {
                setNewPageName(event.target.value as PageNameOption | '');
                setCreateError(null);
              }}
              className="border border-[#EDF0F5] rounded-lg px-3 py-2 text-sm focus:border-[#165bfb] focus:ring-1 focus:ring-[#165bfb] outline-none bg-white"
              disabled={createLoading}
            >
              <option value="">Not set</option>
              {PAGE_NAME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          {createError && (
            <div className="text-xs text-red-600">
              {createError}
            </div>
          )}
          <button
            type="submit"
            className={`h-10 rounded-lg font-semibold text-sm transition-colors ${
              createLoading
                ? 'bg-[#9ebbff] text-white cursor-not-allowed'
                : 'bg-[#165bfb] text-white hover:bg-[#1246d7]'
            }`}
            disabled={createLoading}
          >
            {createLoading ? 'Creating…' : 'Create session'}
          </button>
        </form>
      )}
      {loading && (
        <div className="px-4 py-3 text-sm text-[#6B7280]">Loading...</div>
      )}
      {error && (
        <div className="px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      <ul role="listbox" aria-label="Sessions list">
        {sessions.map((s) => (
          <li
            key={s.id}
            className={`px-4 py-2 flex items-center justify-between gap-2 cursor-pointer hover:bg-[#F6F8FF] ${selectedSession?.id === s.id ? 'bg-[#F6F8FF] font-bold' : ''}`}
            onClick={() => handleSelect(s)}
          >
            <span className="truncate" title={s.name}>{s.name}</span>
            <button
              className="text-[#9b9fa9] hover:text-red-600 text-xs"
              onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
              aria-label={`Delete ${s.name}`}
              title="Delete session"
            >
              ✕
            </button>
          </li>
        ))}
        {sessions.length === 0 && !loading && !error && (
          <li className="px-4 py-3 text-sm text-[#6B7280]">No sessions yet</li>
        )}
      </ul>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="flex items-center gap-2 px-4 py-2 bg-white border border-[#EDF0F5] rounded-lg shadow-sm text-[#165bfb] font-semibold focus:outline-none focus:ring-2 focus:ring-[#165bfb] transition min-w-[130px]"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select session"
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        <span className="truncate max-w-[180px]">{selectedSession ? selectedSession.name : 'No session'}</span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 20 20">
          <path d="M6 8l4 4 4-4" stroke="#165bfb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && typeof window !== 'undefined' && createPortal(dropdownList, document.body)}
    </div>
  );
};

export default SessionDropdown;

